import React, { memo, ReactNode, FC, useMemo, ChangeEvent } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TablePagination, {
  TablePaginationProps,
} from "@mui/material/TablePagination";
import GenDataSortButton from "./GenDataSortButton";
import { ROWS_PER_PAGE_OPTIONS, SortState } from "./types";

export interface GenDataTableRowType {
  key: string;
  isSelected?: boolean;
  data: Record<string, ReactNode>;
  onSelected?: (e: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

export interface GenDataTableColumnType {
  key: string;
  label: string;
  width?: number;
  onSort?: (state: SortState) => void;
}

export interface GenDataTablePaginationProps extends Omit<
  TablePaginationProps,
  "count"
> {
  count?: number;
}

export interface GenDataTableProps {
  rows: GenDataTableRowType[];
  columns: GenDataTableColumnType[];
  fixedColumn?: number;
  fixedColumnWidth?: number;
  multiple?: boolean;
  isAllSelected?: boolean;
  onRowSelected?: (row: GenDataTableRowType) => void;
  onFilter?: (row: GenDataTableRowType[]) => GenDataTableRowType[];
  onAllSelected?: (e: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  paginationProps?: TablePaginationProps & { displayedrows?: boolean };
  paginationPropsStandAlone?: GenDataTablePaginationProps & {
    displayedrows?: boolean;
  };
}

const ROWS_PER_PAGE = 10;

const GenDataTable: FC<GenDataTableProps> = (props) => {
  const {
    rows,
    columns,
    fixedColumn,
    fixedColumnWidth = 100,
    onRowSelected,
    paginationProps,
    paginationPropsStandAlone,
    multiple = false,
    isAllSelected,
    onAllSelected,
  } = props;

  const filteredRows: GenDataTableRowType[] = useMemo(() => {
    if (paginationPropsStandAlone) {
      const { page, rowsPerPage } = paginationPropsStandAlone;
      return rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
    }

    return rows;
  }, [rows, paginationPropsStandAlone]);

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {multiple && (
                <TableCell>
                  <Checkbox checked={isAllSelected} onChange={onAllSelected} />
                </TableCell>
              )}
              {columns.map((column, index) => (
                <TableCell
                  key={`${column.key}-column-${index.toString()}`}
                  width={column.width}
                  sx={
                    fixedColumn && index <= fixedColumn
                      ? {
                          whiteSpace: "nowrap",
                          position: "sticky",
                          background: "white",
                          zIndex: 100,
                          left:
                            index * fixedColumnWidth + (column.onSort ? 15 : 0),
                          marginRight: -2,
                        }
                      : { paddingLeft: 2 }
                  }
                >
                  <Typography
                    noWrap
                    minWidth={
                      fixedColumn && index <= fixedColumn
                        ? (column.width ? column.width : fixedColumnWidth) -
                          (column.onSort ? 15 : 0)
                        : undefined
                    }
                  >
                    {column.label}
                    {column.onSort ? (
                      <GenDataSortButton
                        onClick={(state) => {
                          if (column.onSort) column.onSort(state);
                        }}
                      />
                    ) : undefined}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(paginationPropsStandAlone ? filteredRows : rows).map(
              ({ key: id, data, isSelected, onSelected }, rowIndex) => (
                <>
                  <TableRow
                    key={`${id}-${rowIndex.toString()}`}
                    hover
                    selected={!multiple && isSelected}
                    sx={{
                      "&.MuiTableRow-hover:hover": {
                        backgroundColor: "secondary.light",
                      },
                      "&.Mui-selected": { backgroundColor: "lightpink" },
                    }}
                  >
                    {multiple && (
                      <TableCell key={`${id}-checkbox-${rowIndex.toString()}`}>
                        <Checkbox checked={isSelected} onChange={onSelected} />
                      </TableCell>
                    )}
                    {columns.map(({ key, width, onSort }, index) =>
                      data[key] &&
                      (typeof data[key] === "string" ||
                        (data[key] as any).type === Typography) ? (
                        <TableCell
                          key={`${id}-${key}-${rowIndex.toString()}-${index.toString()}`}
                          onClick={
                            onRowSelected
                              ? () =>
                                  onRowSelected({ key: id, data, isSelected })
                              : undefined
                          }
                          sx={
                            fixedColumn && index <= fixedColumn
                              ? {
                                  whiteSpace: "nowrap",
                                  position: "sticky",
                                  background: "white",
                                  zIndex: 100,
                                  left:
                                    index * fixedColumnWidth +
                                    (onSort ? 15 : 0),
                                  width: width,
                                }
                              : { paddingLeft: 2 }
                          }
                        >
                          <Typography
                            minWidth={
                              fixedColumn && index <= fixedColumn
                                ? width
                                  ? width
                                  : fixedColumnWidth
                                : width
                            }
                          >
                            {data[key]}
                          </Typography>
                        </TableCell>
                      ) : (
                        <TableCell
                          key={`${id}-${key}-${rowIndex.toString()}-${index.toString()}`}
                          sx={
                            fixedColumn && index <= fixedColumn
                              ? {
                                  whiteSpace: "nowrap",
                                  position: "sticky",
                                  background: "white",
                                  zIndex: 100,
                                  left:
                                    index * fixedColumnWidth +
                                    (onSort ? 15 : 0),
                                  width: width,
                                }
                              : { paddingLeft: 2 }
                          }
                        >
                          <Box
                            minWidth={
                              fixedColumn && index <= fixedColumn
                                ? width
                                  ? width
                                  : fixedColumnWidth
                                : undefined
                            }
                          >
                            {data[key]}
                          </Box>
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                </>
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationProps && !paginationPropsStandAlone ? (
        <TablePagination
          {...paginationProps}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          count={paginationProps.count}
          component="div"
          sx={{
            color: "primary.main",
            "& .MuiTablePagination-displayedrows": {
              display:
                paginationProps.displayedrows === false ? "none" : "block",
            },
          }}
        />
      ) : null}
      {paginationPropsStandAlone && !paginationProps ? (
        <TablePagination
          {...paginationPropsStandAlone}
          rowsPerPage={paginationPropsStandAlone.rowsPerPage || ROWS_PER_PAGE}
          rowsPerPageOptions={
            paginationPropsStandAlone.rowsPerPageOptions ||
            ROWS_PER_PAGE_OPTIONS
          }
          component="div"
          count={paginationPropsStandAlone.count || rows.length}
          sx={{
            color: "primary.main",
            "& .MuiTablePagination-displayedrows": {
              display:
                paginationPropsStandAlone.displayedrows === false
                  ? "none"
                  : "block",
            },
          }}
        />
      ) : null}
    </>
  );
};

export default memo(GenDataTable);
